import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BarChart3,
  Sun,
  CloudRain,
  Beaker,
  Wind,
  Droplets,
  TrendingUp,
  ChevronDown,
  Download,
  Mail,
  FileText,
  FileSpreadsheet,
  FileJson,
  Map as MapIcon,
  Layers,
  Radio,
  AlertCircle,
  X,
} from 'lucide-react';
import { useAnalytics } from '../hooks/useAnalytics';
import { useLayout } from '../contexts/LayoutContext';
import { mapFarms } from '../utils/mapMockData';
import { buildFarmGrouping, getSectionIdsForDrill, resolveSectionIdsForStation } from '../utils/areaOverlayUtils';
import { Button, Card, Dropdown, Modal } from '../components/ui';
import { InfoTooltipButton } from '../components/analytics/InfoTooltipButton';
import { StationSelectionHeader } from '../components/analytics/StationSelectionHeader';
import { OverviewTab } from '../components/analytics/OverviewTab';
import { RadiationTab } from '../components/analytics/RadiationTab';
import { ClimateTab } from '../components/analytics/ClimateTab';
import { SoilSensorsTab } from '../components/analytics/SoilSensorsTab';
import { WindTab } from '../components/analytics/WindTab';
import './AnalyticsPage.css';

const MAX_VISIBLE_SECTION_CHIPS = 10;

export function AnalyticsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const activeTab = searchParams.get('tab') || 'overview';

  const {
    selectedFarm,
    selectedSections,
    selectAllSections,
    drillState,
    timeRange,
    loading,
    heroMetrics,
    TIME_RANGES,
    setSelectedSections,
    setSelectAllSections,
    setDrillState,
    setTimeRange,
  } = useAnalytics();
  const { selectedFarm: layoutFarm, selectFarm } = useLayout();

  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [timeRangeDropdownOpen, setTimeRangeDropdownOpen] = useState(false);
  const [exportEmail, setExportEmail] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [mapExpanded, setMapExpanded] = useState(true);
  const [mapClosing, setMapClosing] = useState(false);
  const mapCloseTimeoutRef = useRef(null);
  const [selectedSectionsModalOpen, setSelectedSectionsModalOpen] = useState(false);
  const [stationSelectionUnsavedWarning, setStationSelectionUnsavedWarning] = useState(false);
  const [expandedSectionGroups, setExpandedSectionGroups] = useState(() => ({
    quadrant: true,
    zone: true,
    section: true,
  }));

  // What the analytics map renders: stations (not sections)
  const displayedStations = useMemo(() => {
    return selectedFarm?.stations || [];
  }, [selectedFarm?.stations]);

  const farmGrouping = useMemo(() => {
    return buildFarmGrouping({
      farm: selectedFarm,
      sections: selectedFarm?.sections || [],
      stations: displayedStations,
    });
  }, [selectedFarm, selectedFarm?.sections, displayedStations]);

  const allSectionIds = useMemo(() => {
    return farmGrouping.sections.map(section => section.id).filter(Boolean);
  }, [farmGrouping.sections]);

  const selectedSet = useMemo(() => {
    if (selectAllSections) {
      return new Set(allSectionIds);
    }
    return new Set(Array.isArray(selectedSections) ? selectedSections : []);
  }, [selectAllSections, selectedSections, allSectionIds]);

  useEffect(() => {
    const validIds = new Set(allSectionIds);
    if (allSectionIds.length === 0) {
      if (selectAllSections) setSelectAllSections(false);
      if (selectedSections.length > 0) setSelectedSections([]);
      return;
    }
    const filtered = (Array.isArray(selectedSections) ? selectedSections : []).filter(id => validIds.has(id));
    if (selectAllSections) {
      if (filtered.length !== allSectionIds.length) {
        setSelectedSections(allSectionIds);
      }
      return;
    }
    if (filtered.length !== selectedSections.length) {
      setSelectedSections(filtered);
    }
  }, [allSectionIds, selectAllSections, selectedSections, setSelectAllSections, setSelectedSections]);

  const sectionById = useMemo(() => {
    const map = new Map();
    farmGrouping.sections.forEach((section) => {
      if (section?.id) map.set(section.id, section);
    });
    return map;
  }, [farmGrouping.sections]);

  // Derive unique sections (Section A, Section B) from displayed stations
  const uniqueSections = useMemo(() => {
    const sectionSet = new Set();
    displayedStations.forEach((station) => {
      // Extract section name (e.g., "Section A", "Section B") from locationDescription
      const desc = station.locationDescription || '';
      const match = desc.match(/Section\s+([A-Z])/i);
      if (match) {
        sectionSet.add(`Section ${match[1].toUpperCase()}`);
      }
    });
    // Sort alphabetically (Section A before Section B)
    return Array.from(sectionSet).sort();
  }, [displayedStations]);

  const isStationSelected = (station) => {
    if (!station) return false;
    if (selectAllSections) return true;
    const ids = resolveSectionIdsForStation(selectedFarm, station.id, station, farmGrouping.sections);
    return ids.some(id => selectedSections.includes(id));
  };

  // Get the list of currently selected stations based on section selection
  const selectedStationsList = useMemo(() => {
    return displayedStations.filter((station) => {
      if (selectAllSections) return true;
      const ids = resolveSectionIdsForStation(selectedFarm, station.id, station, farmGrouping.sections);
      return ids.some(id => selectedSections.includes(id));
    });
  }, [displayedStations, selectAllSections, selectedFarm, selectedSections, farmGrouping.sections]);

  const drillSectionIds = useMemo(() => {
    return getSectionIdsForDrill(farmGrouping, drillState);
  }, [farmGrouping, drillState]);

  const drillSectionSet = useMemo(() => {
    return new Set(drillSectionIds);
  }, [drillSectionIds]);

  const baseStations = useMemo(() => {
    if (selectAllSections || selectedSections.length === 0) {
      return displayedStations;
    }
    return selectedStationsList;
  }, [selectAllSections, selectedSections.length, displayedStations, selectedStationsList]);

  const visibleStations = useMemo(() => {
    if (drillSectionIds.length === 0) {
      return baseStations;
    }
    return baseStations.filter((station) => {
      const ids = resolveSectionIdsForStation(selectedFarm, station.id, station, farmGrouping.sections);
      return ids.some(id => drillSectionSet.has(id));
    });
  }, [baseStations, drillSectionIds, drillSectionSet, selectedFarm, farmGrouping.sections]);

  const selectedSectionChips = useMemo(() => {
    if (selectAllSections) return [];
    if (!Array.isArray(selectedSections) || selectedSections.length === 0) return [];

    const sortByName = (a, b) => {
      return (a?.name || '').toString().localeCompare(
        (b?.name || '').toString(),
        undefined,
        { numeric: true, sensitivity: 'base' }
      );
    };

    const getZoneSectionIds = (zone) => {
      const zoneSectionIds = Array.isArray(zone?.sectionIds)
        ? zone.sectionIds.filter(Boolean)
        : [];
      if (zoneSectionIds.length > 0) return zoneSectionIds;
      return Array.isArray(zone?.sections)
        ? zone.sections.map(section => section?.id).filter(Boolean)
        : [];
    };

    const getQuadrantSectionIds = (quadrant) => {
      const quadrantSectionIds = Array.isArray(quadrant?.sectionIds)
        ? quadrant.sectionIds.filter(Boolean)
        : [];
      if (quadrantSectionIds.length > 0) return quadrantSectionIds;
      return Array.isArray(quadrant?.zones)
        ? quadrant.zones.flatMap(zone => getZoneSectionIds(zone))
        : [];
    };

    if (farmGrouping.groupingLevel === 'section') {
      return selectedSections
        .map((id) => {
          const section = sectionById.get(id);
          return {
            id,
            name: section?.areaName || section?.name || id,
            level: 'section',
            sectionIds: [id],
          };
        })
        .sort(sortByName);
    }

    const chips = [];
    const coveredSections = new Set();

    if (farmGrouping.groupingLevel === 'quadrant') {
      const quadrants = Array.isArray(farmGrouping.quadrants) ? farmGrouping.quadrants : [];

      quadrants.forEach((quadrant) => {
        const quadrantSectionIds = getQuadrantSectionIds(quadrant);
        if (quadrantSectionIds.length === 0) return;

        const selectedInQuadrant = quadrantSectionIds.filter(id => selectedSet.has(id));
        if (selectedInQuadrant.length === 0) return;

        if (selectedInQuadrant.length === quadrantSectionIds.length) {
          chips.push({
            id: quadrant.id,
            name: quadrant.areaName || quadrant.name || quadrant.id,
            level: 'quadrant',
            sectionIds: quadrantSectionIds,
          });
          quadrantSectionIds.forEach(id => coveredSections.add(id));
          return;
        }

        const zones = Array.isArray(quadrant.zones) ? quadrant.zones : [];
        zones.forEach((zone) => {
          const zoneSectionIds = getZoneSectionIds(zone);
          if (zoneSectionIds.length === 0) return;

          const selectedInZone = zoneSectionIds.filter(id => selectedSet.has(id));
          if (selectedInZone.length === 0) return;

          if (selectedInZone.length === zoneSectionIds.length) {
            chips.push({
              id: zone.id,
              name: zone.areaName || zone.name || zone.id,
              level: 'zone',
              sectionIds: zoneSectionIds,
            });
            zoneSectionIds.forEach(id => coveredSections.add(id));
            return;
          }

          selectedInZone.forEach((sectionId) => {
            const section = sectionById.get(sectionId);
            chips.push({
              id: sectionId,
              name: section?.areaName || section?.name || sectionId,
              level: 'section',
              sectionIds: [sectionId],
            });
            coveredSections.add(sectionId);
          });
        });
      });
    } else {
      const zones = Array.isArray(farmGrouping.zones) ? farmGrouping.zones : [];

      zones.forEach((zone) => {
        const zoneSectionIds = getZoneSectionIds(zone);
        if (zoneSectionIds.length === 0) return;

        const selectedInZone = zoneSectionIds.filter(id => selectedSet.has(id));
        if (selectedInZone.length === 0) return;

        if (selectedInZone.length === zoneSectionIds.length) {
          chips.push({
            id: zone.id,
            name: zone.areaName || zone.name || zone.id,
            level: 'zone',
            sectionIds: zoneSectionIds,
          });
          zoneSectionIds.forEach(id => coveredSections.add(id));
          return;
        }

        selectedInZone.forEach((sectionId) => {
          const section = sectionById.get(sectionId);
          chips.push({
            id: sectionId,
            name: section?.areaName || section?.name || sectionId,
            level: 'section',
            sectionIds: [sectionId],
          });
          coveredSections.add(sectionId);
        });
      });
    }

    selectedSections.forEach((sectionId) => {
      if (coveredSections.has(sectionId)) return;
      const section = sectionById.get(sectionId);
      chips.push({
        id: sectionId,
        name: section?.areaName || section?.name || sectionId,
        level: 'section',
        sectionIds: [sectionId],
      });
    });

    return chips.sort(sortByName);
  }, [
    selectAllSections,
    selectedSections,
    farmGrouping.groupingLevel,
    farmGrouping.quadrants,
    farmGrouping.zones,
    sectionById,
    selectedSet,
  ]);

  const visibleSelectedSectionChips = useMemo(() => {
    return selectedSectionChips.slice(0, MAX_VISIBLE_SECTION_CHIPS);
  }, [selectedSectionChips]);

  const remainingSelectedSectionChips = Math.max(
    selectedSectionChips.length - visibleSelectedSectionChips.length,
    0
  );

  const groupedSectionChips = useMemo(() => {
    const groups = {
      quadrant: [],
      zone: [],
      section: [],
    };

    selectedSectionChips.forEach((chip) => {
      if (!chip) return;
      const level = chip.level || 'section';
      if (!groups[level]) {
        groups[level] = [];
      }
      groups[level].push(chip);
    });

    const sortByName = (a, b) => {
      return (a?.name || '').toString().localeCompare(
        (b?.name || '').toString(),
        undefined,
        { numeric: true, sensitivity: 'base' }
      );
    };

    return [
      {
        level: 'quadrant',
        label: t('analytics.sections.levels.quadrant', { defaultValue: 'Quadrants' }),
        items: groups.quadrant.sort(sortByName),
      },
      {
        level: 'zone',
        label: t('analytics.sections.levels.zone', { defaultValue: 'Zones' }),
        items: groups.zone.sort(sortByName),
      },
      {
        level: 'section',
        label: t('analytics.sections.levels.section', { defaultValue: 'Sections' }),
        items: groups.section.sort(sortByName),
      },
    ].filter((group) => group.items.length > 0);
  }, [selectedSectionChips, t]);

  const selectedSectionCount = selectAllSections ? allSectionIds.length : selectedSections.length;
  const selectedStationCount = selectAllSections ? displayedStations.length : selectedStationsList.length;
  const selectedSectionsModalTitle = t('analytics.sections.selectedSectionsWithCount', {
    count: selectedSectionCount,
    defaultValue: `Selected sections (${selectedSectionCount})`,
  });
  const selectedSectionsModalSummary = t('analytics.sections.countSummary', {
    sections: selectedSectionCount,
    stations: selectedStationCount,
    defaultValue: `Sections: ${selectedSectionCount} · Stations: ${selectedStationCount}`,
  });

  const applySectionSelection = (nextIds) => {
    const validIds = new Set(allSectionIds);
    const uniqueIds = Array.from(new Set((nextIds || []).filter(id => id && validIds.has(id))));
    const uniqueSet = new Set(uniqueIds);
    const isAllNow = allSectionIds.length > 0 && allSectionIds.every(id => uniqueSet.has(id));
    if (isAllNow) {
      setSelectAllSections(true);
      setSelectedSections(allSectionIds);
    } else {
      setSelectAllSections(false);
      setSelectedSections(uniqueIds);
    }
  };

  const toggleSectionFromChip = (sectionIds = []) => {
    const ids = (Array.isArray(sectionIds) ? sectionIds : [sectionIds]).filter(Boolean);
    if (ids.length === 0) return;

    if (selectAllSections) {
      const remaining = allSectionIds.filter(id => !ids.includes(id));
      applySectionSelection(remaining);
      return;
    }

    const selectedCount = ids.filter(id => selectedSet.has(id)).length;
    const isFullySelected = selectedCount === ids.length;
    const nextSelection = isFullySelected
      ? selectedSections.filter(id => !ids.includes(id))
      : Array.from(new Set([...(selectedSections || []), ...ids]));
    applySectionSelection(nextSelection);
  };

  const clearSelectedSections = () => {
    setSelectAllSections(false);
    setSelectedSections([]);
    setDrillState(null);
  };

  const selectAllCurrentSections = () => {
    setSelectAllSections(true);
    setSelectedSections(allSectionIds);
    setDrillState(null);
  };

  const toggleSectionGroup = (level) => {
    setExpandedSectionGroups((prev) => ({
      ...prev,
      [level]: !prev[level],
    }));
  };

  // Deep link from Map/Stations: open Analytics for a specific station.
  // We need to (1) switch to the correct farm, and (2) select the correct Section(s) for that station.
  useEffect(() => {
    const stationId = location.state?.stationId;
    if (!stationId) return;

    // Find station across all farms
    let foundStation = null;
    let foundFarm = null;
    for (const farm of mapFarms) {
      const station = farm.stations?.find(s => s.id === stationId);
      if (station) {
        foundStation = station;
        foundFarm = farm;
        break;
      }
    }

    if (!foundStation || !foundFarm) {
      // Clear state so we don't keep retrying
      try { window.history.replaceState({}, document.title); } catch (e) {}
      return;
    }

    // Ensure LayoutContext farm matches the station's farm
    if (!layoutFarm || layoutFarm.id !== foundFarm.id) {
      selectFarm(foundFarm);
      // Wait for selectedFarm to update before selecting sections
      return;
    }

    // If AnalyticsContext farm isn't in sync yet, wait one render
    if (!selectedFarm || selectedFarm.id !== foundFarm.id) {
      return;
    }

    // Deterministically map station -> section(s) using section.stationIds (split mode)
    // or section.id === station.id (legacy mode).
    const sectionIds = resolveSectionIdsForStation(selectedFarm, stationId, foundStation, farmGrouping.sections);
    if (sectionIds.length > 0) {
      setSelectAllSections(false);
      setSelectedSections(sectionIds);
    } else {
      // Safety fallback: select all (so user never lands on an empty state)
      setSelectAllSections(true);
      setSelectedSections(allSectionIds);
    }

    // Clear route state to avoid reapplying on re-render
    try { window.history.replaceState({}, document.title); } catch (e) {}
  }, [
    location.state,
    layoutFarm?.id,
    selectFarm,
    selectedFarm?.id,
    allSectionIds,
    setSelectAllSections,
    setSelectedSections
  ]);
  
  const handleInstantDownload = (format) => {
    const exportPayload = {
      format,
      exportType: 'instant_download',
      timeRange: {
        id: timeRange.id,
        days: timeRange.days,
        label: timeRangeLabel,
      },
      farm: {
        id: selectedFarm?.id || null,
        name: selectedFarm?.name || null,
      },
      sections: {
        selectAll: selectAllSections,
        ids: Array.isArray(selectedSections) ? selectedSections : [],
      },
      activeTab,
      generatedAt: new Date().toISOString(),
    };
    console.log('Analytics export download requested', exportPayload);
    // TODO: Implement actual download
    setExportDropdownOpen(false);
  };

  const handleEmailReport = async () => {
    if (!exportEmail || !exportEmail.includes('@')) {
      alert(t('validation.invalidEmail'));
      return;
    }
    const exportPayload = {
      exportType: 'email_report',
      recipientEmail: exportEmail.trim(),
      timeRange: {
        id: timeRange.id,
        days: timeRange.days,
        label: timeRangeLabel,
      },
      farm: {
        id: selectedFarm?.id || null,
        name: selectedFarm?.name || null,
      },
      sections: {
        selectAll: selectAllSections,
        ids: Array.isArray(selectedSections) ? selectedSections : [],
      },
      activeTab,
      requestedAt: new Date().toISOString(),
    };
    console.log('Analytics email report requested', exportPayload);
    setEmailSending(true);
    // Simulate sending email
    setTimeout(() => {
      setEmailSending(false);
      setEmailSent(true);
      setTimeout(() => {
        setExportDropdownOpen(false);
        setEmailSent(false);
        setExportEmail('');
      }, 2000);
    }, 1500);
  };

  // V2 Tab Structure: Overview, Soil & Sensors, Climate, Radiation, Wind
  // Advanced tab is hidden for now
  const tabs = useMemo(() => ([
    { id: 'overview', label: t('analytics.tabs.overview'), icon: BarChart3, tooltip: t('analytics.tabTooltips.overview') },
    { id: 'soil-sensors', label: t('analytics.tabs.soil'), icon: Beaker, tooltip: t('analytics.tabTooltips.soil') },
    { id: 'climate', label: t('analytics.tabs.climate'), icon: CloudRain, tooltip: t('analytics.tabTooltips.climate') },
    { id: 'radiation', label: t('analytics.tabs.radiation'), icon: Sun, tooltip: t('analytics.tabTooltips.radiation') },
    { id: 'wind', label: t('analytics.tabs.wind'), icon: Wind, tooltip: t('analytics.tabTooltips.wind') },
  ]), [t]);

  const timeRangeLabel = useMemo(() => {
    const key = `analytics.timeRanges.${timeRange.id}`;
    return t(key, { defaultValue: timeRange.label });
  }, [t, timeRange.id, timeRange.label]);

  const handleTabChange = (tabId) => {
    setSearchParams({ tab: tabId });
  };

  useEffect(() => {
    return () => {
      if (mapCloseTimeoutRef.current) {
        clearTimeout(mapCloseTimeoutRef.current);
        mapCloseTimeoutRef.current = null;
      }
    };
  }, []);

  const setMapView = (shouldShowMap) => {
    if (mapCloseTimeoutRef.current) {
      clearTimeout(mapCloseTimeoutRef.current);
      mapCloseTimeoutRef.current = null;
    }
    if (shouldShowMap) {
      setMapExpanded(true);
      setMapClosing(false);
      return;
    }
    if (!mapExpanded) {
      setMapClosing(false);
      return;
    }
    if (!mapClosing) {
      setMapClosing(true);
      mapCloseTimeoutRef.current = setTimeout(() => {
        setMapExpanded(false);
        setMapClosing(false);
        mapCloseTimeoutRef.current = null;
      }, 450); // Match CSS animation duration
    }
  };

  const mapViewLabel = t('analytics.selector.mapView', { defaultValue: 'Map' });
  const treeViewLabel = t('analytics.selector.treeView', { defaultValue: 'Tree' });


  return (
    <div className="analytics-page">
      {/* Hero Dashboard */}
      <div className="analytics-hero">
        {/* Hero Metrics Section Header */}
        <div className="dashboard__section-header">
          <h2 className="dashboard__section-title">{t('analytics.keyMetrics')}</h2>
          <p className="dashboard__section-subtitle">{t('analytics.hero.keyMetricsSubtitle')}</p>
        </div>
        {/* Hero Metrics - Unified Section */}
        <div className="analytics-hero__metrics-container">
          <div className="analytics-hero__metrics">
          {loading || !heroMetrics ? (
            // Loading skeletons
            Array.from({ length: 5 }).map((_, i) => (
              <Card key={i} className="analytics-hero-metric" padding="md">
                <div className="analytics-hero-metric__skeleton" />
              </Card>
            ))
          ) : (
            <>
              {/* Metric 1: Average GHI */}
              <Card className="analytics-hero-metric" padding="md" hoverable>
                <div className="analytics-hero-metric__header">
                  <Sun size={28} className="analytics-hero-metric__icon analytics-hero-metric__icon--primary" />
                  <InfoTooltipButton content={t('analytics.tooltips.avgGhi')} />
                </div>
                <div className="analytics-hero-metric__label">{t('analytics.metrics.radiation')}</div>
                <div className="analytics-hero-metric__data-grid">
                  <div className="analytics-hero-metric__data-item">
                    <span className="analytics-hero-metric__data-label">{t('analytics.labels.field')}</span>
                    <span className="analytics-hero-metric__data-value">
                      {heroMetrics.avgGhiField}
                      <span className="analytics-hero-metric__unit">kWh/m²</span>
                    </span>
                  </div>
                  <div className="analytics-hero-metric__data-item">
                    <span className="analytics-hero-metric__data-label">{t('analytics.labels.under')}</span>
                    <span className="analytics-hero-metric__data-value">
                      {heroMetrics.avgGhiUnder}
                      <span className="analytics-hero-metric__unit">kWh/m²</span>
                    </span>
                  </div>
                  <div className="analytics-hero-metric__data-item">
                    <span className="analytics-hero-metric__data-label">{t('analytics.labels.average')}</span>
                    <span className="analytics-hero-metric__data-value">
                      {((parseFloat(heroMetrics.avgGhiField) + parseFloat(heroMetrics.avgGhiUnder)) / 2).toFixed(2)}
                      <span className="analytics-hero-metric__unit">kWh/m²</span>
                    </span>
                  </div>
                  <div className="analytics-hero-metric__data-item analytics-hero-metric__data-item--delta">
                    <span className="analytics-hero-metric__data-label">{t('analytics.labels.delta')}</span>
                    <span className="analytics-hero-metric__data-value analytics-hero-metric__delta analytics-hero-metric__delta--negative">
                      {heroMetrics.ghiDelta}%
                    </span>
                  </div>
                </div>
              </Card>

              {/* Metric 2: Average Temperature */}
              <Card className="analytics-hero-metric" padding="md" hoverable>
                <div className="analytics-hero-metric__header">
                  <CloudRain size={28} className="analytics-hero-metric__icon analytics-hero-metric__icon--success" />
                  <InfoTooltipButton content={t('analytics.tooltips.avgTemp')} />
                </div>
                <div className="analytics-hero-metric__label">{t('analytics.metrics.temperature')}</div>
                <div className="analytics-hero-metric__data-grid">
                  <div className="analytics-hero-metric__data-item">
                    <span className="analytics-hero-metric__data-label">{t('analytics.labels.field')}</span>
                    <span className="analytics-hero-metric__data-value">
                      {heroMetrics.avgTempField}
                      <span className="analytics-hero-metric__unit">°C</span>
                    </span>
                  </div>
                  <div className="analytics-hero-metric__data-item">
                    <span className="analytics-hero-metric__data-label">{t('analytics.labels.under')}</span>
                    <span className="analytics-hero-metric__data-value">
                      {heroMetrics.avgTempUnder}
                      <span className="analytics-hero-metric__unit">°C</span>
                    </span>
                  </div>
                  <div className="analytics-hero-metric__data-item">
                    <span className="analytics-hero-metric__data-label">{t('analytics.labels.average')}</span>
                    <span className="analytics-hero-metric__data-value">
                      {((parseFloat(heroMetrics.avgTempField) + parseFloat(heroMetrics.avgTempUnder)) / 2).toFixed(1)}
                      <span className="analytics-hero-metric__unit">°C</span>
                    </span>
                  </div>
                  <div className="analytics-hero-metric__data-item analytics-hero-metric__data-item--delta">
                    <span className="analytics-hero-metric__data-label">{t('analytics.labels.delta')}</span>
                    <span className={`analytics-hero-metric__data-value analytics-hero-metric__delta ${parseFloat(heroMetrics.tempDelta) < 0 ? 'analytics-hero-metric__delta--positive' : 'analytics-hero-metric__delta--negative'}`}>
                      {heroMetrics.tempDelta}°C
                    </span>
                  </div>
                </div>
              </Card>

              {/* Metric 3: Crop ET */}
              <Card className="analytics-hero-metric" padding="md" hoverable>
                <div className="analytics-hero-metric__header">
                  <Droplets size={28} className="analytics-hero-metric__icon analytics-hero-metric__icon--info" />
                  <InfoTooltipButton content={t('analytics.tooltips.totalEtc')} />
                </div>
                <div className="analytics-hero-metric__label">{t('analytics.metrics.evapotranspiration')}</div>
                <div className="analytics-hero-metric__data-grid">
                  <div className="analytics-hero-metric__data-item">
                    <span className="analytics-hero-metric__data-label">{t('analytics.labels.field')}</span>
                    <span className="analytics-hero-metric__data-value">
                      {heroMetrics.totalEtcField}
                      <span className="analytics-hero-metric__unit">mm</span>
                    </span>
                  </div>
                  <div className="analytics-hero-metric__data-item">
                    <span className="analytics-hero-metric__data-label">{t('analytics.labels.under')}</span>
                    <span className="analytics-hero-metric__data-value">
                      {heroMetrics.totalEtcUnder}
                      <span className="analytics-hero-metric__unit">mm</span>
                    </span>
                  </div>
                  <div className="analytics-hero-metric__data-item">
                    <span className="analytics-hero-metric__data-label">{t('analytics.labels.average')}</span>
                    <span className="analytics-hero-metric__data-value">
                      {((parseFloat(heroMetrics.totalEtcField) + parseFloat(heroMetrics.totalEtcUnder)) / 2).toFixed(1)}
                      <span className="analytics-hero-metric__unit">mm</span>
                    </span>
                  </div>
                  <div className="analytics-hero-metric__data-item analytics-hero-metric__data-item--delta">
                    <span className="analytics-hero-metric__data-label">{t('analytics.labels.delta')}</span>
                    <span className="analytics-hero-metric__data-value analytics-hero-metric__delta analytics-hero-metric__delta--positive">
                      {heroMetrics.etcDelta}%
                    </span>
                  </div>
                </div>
              </Card>

              {/* Metric 4: Total Rainfall */}
              <Card className="analytics-hero-metric" padding="md" hoverable>
                <div className="analytics-hero-metric__header">
                  <CloudRain size={28} className="analytics-hero-metric__icon analytics-hero-metric__icon--warning" />
                  <InfoTooltipButton content={t('analytics.tooltips.totalRain')} />
                </div>
                <div className="analytics-hero-metric__label">{t('analytics.metrics.rainfall')}</div>
                <div className="analytics-hero-metric__data-grid">
                  <div className="analytics-hero-metric__data-item">
                    <span className="analytics-hero-metric__data-label">{t('analytics.labels.field')}</span>
                    <span className="analytics-hero-metric__data-value">
                      {heroMetrics.totalRainField}
                      <span className="analytics-hero-metric__unit">mm</span>
                    </span>
                  </div>
                  <div className="analytics-hero-metric__data-item">
                    <span className="analytics-hero-metric__data-label">{t('analytics.labels.under')}</span>
                    <span className="analytics-hero-metric__data-value">
                      {heroMetrics.totalRainUnder}
                      <span className="analytics-hero-metric__unit">mm</span>
                    </span>
                  </div>
                  <div className="analytics-hero-metric__data-item">
                    <span className="analytics-hero-metric__data-label">{t('analytics.labels.average')}</span>
                    <span className="analytics-hero-metric__data-value">
                      {((parseFloat(heroMetrics.totalRainField) + parseFloat(heroMetrics.totalRainUnder)) / 2).toFixed(1)}
                      <span className="analytics-hero-metric__unit">mm</span>
                    </span>
                  </div>
                  <div className="analytics-hero-metric__data-item analytics-hero-metric__data-item--delta">
                    <span className="analytics-hero-metric__data-label">{t('analytics.labels.delta')}</span>
                    <span className="analytics-hero-metric__data-value">
                      {((parseFloat(heroMetrics.totalRainUnder) - parseFloat(heroMetrics.totalRainField)).toFixed(1))}
                      <span className="analytics-hero-metric__unit">mm</span>
                    </span>
                  </div>
                </div>
              </Card>

              {/* Metric 5: Average VPD */}
              <Card className="analytics-hero-metric" padding="md" hoverable>
                <div className="analytics-hero-metric__header">
                  <TrendingUp size={28} className="analytics-hero-metric__icon analytics-hero-metric__icon--danger" />
                  <InfoTooltipButton content={t('analytics.tooltips.avgVpd')} />
                </div>
                <div className="analytics-hero-metric__label">{t('analytics.metrics.vpd')}</div>
                <div className="analytics-hero-metric__data-grid">
                  <div className="analytics-hero-metric__data-item">
                    <span className="analytics-hero-metric__data-label">{t('analytics.labels.field')}</span>
                    <span className="analytics-hero-metric__data-value">
                      {heroMetrics.avgVpdField}
                      <span className="analytics-hero-metric__unit">kPa</span>
                    </span>
                  </div>
                  <div className="analytics-hero-metric__data-item">
                    <span className="analytics-hero-metric__data-label">{t('analytics.labels.under')}</span>
                    <span className="analytics-hero-metric__data-value">
                      {heroMetrics.avgVpdUnder}
                      <span className="analytics-hero-metric__unit">kPa</span>
                    </span>
                  </div>
                  <div className="analytics-hero-metric__data-item">
                    <span className="analytics-hero-metric__data-label">{t('analytics.labels.average')}</span>
                    <span className="analytics-hero-metric__data-value">
                      {((parseFloat(heroMetrics.avgVpdField) + parseFloat(heroMetrics.avgVpdUnder)) / 2).toFixed(2)}
                      <span className="analytics-hero-metric__unit">kPa</span>
                    </span>
                  </div>
                  <div className="analytics-hero-metric__data-item analytics-hero-metric__data-item--delta">
                    <span className="analytics-hero-metric__data-label">{t('analytics.labels.delta')}</span>
                    <span className="analytics-hero-metric__data-value analytics-hero-metric__delta analytics-hero-metric__delta--positive">
                      {(parseFloat(heroMetrics.avgVpdUnder) - parseFloat(heroMetrics.avgVpdField)).toFixed(2)}kPa
                    </span>
                  </div>
                </div>
              </Card>
            </>
          )}
          </div>
        </div>
      </div>

      {/* Analytics Categories Section */}
      <div className="dashboard__section-header">
        <h2 className="dashboard__section-title">{t('analytics.categories.title')}</h2>
        <p className="dashboard__section-subtitle">{t('analytics.categories.subtitle')}</p>
      </div>

      {/* Unified Header Section */}
      <div className="analytics-header-section">
        {/* Section Header */}
        <div className="analytics-header-section__header">
          <div className="analytics-header-section__header-content">
            <Radio size={28} className="analytics-header-section__header-icon" />
            <div className="analytics-header-section__header-text">
              <h3 className="analytics-header-section__header-title">{t('analytics.sections.selectTitle')}</h3>
              <p className="analytics-header-section__header-subtitle">{t('analytics.sections.selectSubtitle')}</p>
            </div>
          </div>
          <div className="analytics-header-section__header-counter">
            {visibleStations.length === 0
              ? t('analytics.stations.noStations', { defaultValue: 'No stations' })
              : visibleStations.length === 1
              ? t('analytics.stations.oneActive', { defaultValue: '1 Active Station' })
              : t('analytics.stations.manyActive', { count: visibleStations.length, defaultValue: `${visibleStations.length} Active Stations` })}
          </div>
        </div>

        {/* Selected Sections Summary - interactive chips with counts */}
        <div className="analytics-selected-sensors">
          <div className="analytics-selected-sensors__label">
            <span className="analytics-selected-sensors__label-text">
              {t('analytics.sections.selectedSections', { defaultValue: 'Selected sections' })}
            </span>
            <span className="analytics-selected-sensors__summary-text">
              {selectAllSections
                ? t('analytics.sections.allSections', { defaultValue: 'All sections' })
                : t('analytics.sections.countSummary', {
                  sections: selectedSectionCount,
                  stations: selectedStationCount,
                  defaultValue: `Sections: ${selectedSectionCount} · Stations: ${selectedStationCount}`,
                })}
            </span>
          </div>
          <div className="analytics-selected-sensors__actions">
            <button
              type="button"
              className="analytics-header-section__action-button"
              onClick={selectAllCurrentSections}
              aria-pressed={selectAllSections}
              title={t('common.all')}
            >
              <span>{t('common.all')}</span>
            </button>
            <button
              type="button"
              className="analytics-header-section__action-button"
              onClick={clearSelectedSections}
              disabled={selectedSections.length === 0 && !selectAllSections}
              title={t('common.clear')}
            >
              <span>{t('common.clear')}</span>
            </button>
          </div>
          <div className="analytics-selected-sensors__chips">
            {selectAllSections ? (
              <button
                type="button"
                className="analytics-selected-sensors__chip analytics-selected-sensors__chip--all"
                onClick={clearSelectedSections}
                aria-label={t('common.clear', { defaultValue: 'Clear' })}
                title={t('common.clear', { defaultValue: 'Clear' })}
              >
                <span className="analytics-selected-sensors__chip-indicator" />
                <span className="analytics-selected-sensors__chip-name">
                  {t('analytics.sections.allSections', { defaultValue: 'All sections' })}
                </span>
                <span className="analytics-selected-sensors__chip-clear" aria-hidden="true">
                  <X size={12} />
                  <span>{t('common.clear', { defaultValue: 'Clear' })}</span>
                </span>
              </button>
            ) : selectedSectionChips.length > 0 ? (
              <>
                {visibleSelectedSectionChips.map((section) => (
                  <button
                    type="button"
                    key={section.id}
                    className="analytics-selected-sensors__chip"
                    onClick={() => toggleSectionFromChip(section.sectionIds)}
                    aria-label={`${t('common.remove', { defaultValue: 'Remove' })} ${section.name}`}
                    title={`${t('common.remove', { defaultValue: 'Remove' })} ${section.name}`}
                  >
                    <span className="analytics-selected-sensors__chip-indicator" />
                    <span className="analytics-selected-sensors__chip-name">
                      {section.name}
                    </span>
                    <span className="analytics-selected-sensors__chip-remove" aria-hidden="true">
                      <X size={12} />
                    </span>
                  </button>
                ))}
                {remainingSelectedSectionChips > 0 && (
                  <button
                    type="button"
                    className="analytics-selected-sensors__chip analytics-selected-sensors__chip--more"
                    onClick={() => setSelectedSectionsModalOpen(true)}
                    aria-haspopup="dialog"
                    aria-expanded={selectedSectionsModalOpen}
                    aria-controls="selected-sections-modal"
                    aria-label={t('analytics.sections.showMore', {
                      count: remainingSelectedSectionChips,
                      defaultValue: `Show ${remainingSelectedSectionChips} more selected sections`,
                    })}
                  >
                    <span className="analytics-selected-sensors__chip-more-text">
                      +{remainingSelectedSectionChips} {t('common.more', { defaultValue: 'more' })}
                    </span>
                  </button>
                )}
              </>
            ) : (
              <div className="analytics-selected-sensors__empty">
                {t('analytics.sections.noSelection', { defaultValue: 'No sections selected' })}
              </div>
            )}
          </div>
        </div>

        <Modal
          isOpen={selectedSectionsModalOpen}
          onClose={() => setSelectedSectionsModalOpen(false)}
          size="md"
          title={selectedSectionsModalTitle}
          footer={(
            <Button type="button" variant="secondary" onClick={() => setSelectedSectionsModalOpen(false)}>
              {t('analytics.sections.showLess', { defaultValue: 'Show less' })}
            </Button>
          )}
          id="selected-sections-modal"
          className="analytics-selected-sections-modal"
        >
          <div className="analytics-selected-sections-modal__summary">
            {selectedSectionsModalSummary}
          </div>
          <div className="analytics-selected-sections-modal__groups">
            {groupedSectionChips.length === 0 ? (
              <div className="analytics-selected-sections-modal__empty">
                {t('analytics.sections.noSelection', { defaultValue: 'No sections selected' })}
              </div>
            ) : (
              groupedSectionChips.map((group) => {
                const isExpanded = !!expandedSectionGroups[group.level];
                const groupId = `selected-sections-group-${group.level}`;
                return (
                  <div className="analytics-selected-sections-modal__group" key={group.level}>
                    <button
                      type="button"
                      className="analytics-selected-sections-modal__group-header"
                      onClick={() => toggleSectionGroup(group.level)}
                      aria-expanded={isExpanded}
                      aria-controls={groupId}
                    >
                      <div className="analytics-selected-sections-modal__group-header-left">
                        <span className="analytics-selected-sections-modal__group-title">
                          {group.label}
                        </span>
                        <span className="analytics-selected-sections-modal__group-count">
                          {group.items.length}
                        </span>
                      </div>
                      <ChevronDown
                        size={18}
                        className={`analytics-selected-sections-modal__group-chevron ${isExpanded ? 'is-expanded' : ''}`}
                      />
                    </button>
                    {isExpanded && (
                      <div className="analytics-selected-sections-modal__group-content" id={groupId}>
                        <div className="analytics-selected-sections-modal__chip-list">
                          {group.items.map((section) => (
                            <button
                              type="button"
                              key={`${group.level}-${section.id}`}
                              className="analytics-selected-sensors__chip analytics-selected-sections-modal__chip"
                              onClick={() => toggleSectionFromChip(section.sectionIds)}
                              aria-label={`${t('common.remove', { defaultValue: 'Remove' })} ${section.name}`}
                              title={`${t('common.remove', { defaultValue: 'Remove' })} ${section.name}`}
                            >
                              <span className="analytics-selected-sensors__chip-indicator" />
                              <span className="analytics-selected-sensors__chip-name">
                                {section.name}
                              </span>
                              <span className="analytics-selected-sensors__chip-remove" aria-hidden="true">
                                <X size={12} />
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </Modal>

        {/* Top Row: Filters & Actions */}
        <div className="analytics-header-section__top-bar">
          <div className="analytics-header-section__filter-group">
            <div className="station-selection-header__toggle-buttons">
              <button
                type="button"
                className={`station-selection-header__toggle-button ${mapExpanded ? 'station-selection-header__toggle-button--active' : ''}`}
                onClick={() => setMapView(true)}
                aria-pressed={mapExpanded}
              >
              <MapIcon size={16} />
                <span>{mapViewLabel}</span>
              </button>
              <button
                type="button"
                className={`station-selection-header__toggle-button ${!mapExpanded ? 'station-selection-header__toggle-button--active' : ''}`}
                onClick={() => setMapView(false)}
                aria-pressed={!mapExpanded}
              >
              <Layers size={16} />
                <span>{treeViewLabel}</span>
              </button>
            </div>
          </div>
          <div className="analytics-header-section__actions">
            <div className="analytics-header-section__filter-group" style={{ marginRight: 'var(--spacing-3)' }}>
              {stationSelectionUnsavedWarning && (
                <div className="station-selection-header__unsaved-warning">
                  <AlertCircle size={16} className="station-selection-header__unsaved-icon" />
                  <span className="station-selection-header__unsaved-text">
                    {t('common.unsavedChangesShort')}
                  </span>
                </div>
              )}
              <Dropdown
                isOpen={timeRangeDropdownOpen}
                onToggle={(open) => setTimeRangeDropdownOpen(open)}
                trigger={
                  <button
                    type="button"
                    className="analytics-header-section__filter-button"
                    title={t('analytics.timeRange.placeholder')}
                    aria-label={t('analytics.timeRange.placeholder')}
                  >
                    <span>{timeRangeLabel}</span>
                    <ChevronDown size={14} className="station-selection-header__chevron-icon" />
                  </button>
                }
                items={TIME_RANGES.map((range) => ({
                  label: t(`analytics.timeRanges.${range.id}`, { defaultValue: range.label }),
                  active: timeRange?.id === range.id,
                  onClick: () => {
                    setTimeRange(range);
                    setTimeRangeDropdownOpen(false);
                  },
                }))}
                align="left"
              />
            </div>
            <Dropdown
              isOpen={exportDropdownOpen}
              onToggle={(open) => setExportDropdownOpen(open)}
              trigger={
                <button
                  className="analytics-header-section__action-button"
                  title={t('analytics.export.buttonTitle')}
                  aria-label={t('analytics.export.button')}
                >
                  <Download size={16} />
                  <span>{t('analytics.export.button')}</span>
                </button>
              }
            >
              <div className="analytics-export-dropdown">
                {/* Instant Download Section */}
                <div className="analytics-export-section">
                  <div className="analytics-export-section__title">
                    <Download size={14} />
                    <span>{t('analytics.export.instantDownload')}</span>
                  </div>
                  <button
                    className="analytics-export-item"
                    onClick={() => handleInstantDownload('csv')}
                  >
                    <FileText size={16} />
                    <div className="analytics-export-item__content">
                      <span className="analytics-export-item__label">{t('analytics.export.formats.csv')}</span>
                      <span className="analytics-export-item__desc">{t('analytics.export.csvDesc')}</span>
                    </div>
                  </button>
                  <button
                    className="analytics-export-item"
                    onClick={() => handleInstantDownload('excel')}
                  >
                    <FileSpreadsheet size={16} />
                    <div className="analytics-export-item__content">
                      <span className="analytics-export-item__label">{t('analytics.export.formats.excel')}</span>
                      <span className="analytics-export-item__desc">{t('analytics.export.excelDesc')}</span>
                    </div>
                  </button>
                  <button
                    className="analytics-export-item"
                    onClick={() => handleInstantDownload('json')}
                  >
                    <FileJson size={16} />
                    <div className="analytics-export-item__content">
                      <span className="analytics-export-item__label">{t('analytics.export.formats.json')}</span>
                      <span className="analytics-export-item__desc">{t('analytics.export.jsonDesc')}</span>
                    </div>
                  </button>
                </div>

                {/* Email Report Section */}
                <div className="analytics-export-section analytics-export-section--email">
                  <div className="analytics-export-section__title">
                    <Mail size={14} />
                    <span>{t('analytics.export.emailReport')}</span>
                  </div>
                  {emailSent ? (
                    <div className="analytics-export-success">
                      {t('analytics.export.sentTo', { email: exportEmail })}
                    </div>
                  ) : (
                    <>
                      <p className="analytics-export-email-desc">
                        {t('analytics.export.emailReportDesc')}
                      </p>
                      <div className="analytics-export-email-form">
                        <input
                          type="email"
                          placeholder={t('analytics.export.emailPlaceholder')}
                          value={exportEmail}
                          onChange={(e) => setExportEmail(e.target.value)}
                          className="analytics-export-email-input"
                        />
                        <button
                          className="analytics-export-email-button"
                          onClick={handleEmailReport}
                          disabled={emailSending || !exportEmail}
                        >
                          {emailSending ? t('analytics.export.sending') : t('analytics.export.send')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </Dropdown>
          </div>
        </div>

        {/* Middle Row: Sections Controls */}
        <div className="analytics-header-section__sections-bar">
          <StationSelectionHeader
            selectedSections={selectedSections}
            selectAllSections={selectAllSections}
            onSectionsChange={setSelectedSections}
            onSelectAllChange={setSelectAllSections}
            mapExpanded={mapExpanded}
            mapClosing={mapClosing}
            onMapToggle={setMapView}
            showActions={false}
            showViewToggle={false}
            showUnsavedWarning={false}
            onUnsavedChangesChange={setStationSelectionUnsavedWarning}
          />
        </div>
      </div>

      {/* Tab Content */}
      <div className="analytics-tab-content-wrapper">
        {/* Tab Navigation */}
        <div className="analytics-header-section__tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`analytics-tab ${activeTab === tab.id ? 'analytics-tab--active' : ''}`}
                onClick={() => handleTabChange(tab.id)}
                title={tab.tooltip}
                aria-label={`${tab.label}. ${tab.tooltip}`}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
        <div className="analytics-tab-content">
          {/* Tab Content */}
          {activeTab === 'overview' && <OverviewTab />}

          {activeTab === 'soil-sensors' && <SoilSensorsTab />}

          {activeTab === 'climate' && <ClimateTab />}

          {activeTab === 'radiation' && <RadiationTab />}

          {activeTab === 'wind' && <WindTab />}
        </div>
      </div>
    </div>
  );
}
